// Bake-layout helpers for the Sankey chart.
//
// The d3-sankey library produces a natural layout (x0/x1/y0/y1) at zoom=1.
// On top of that we apply:
//   - vertical-only zoom: every column fills the same vertical extent (= availH × k)
//   - focus mode (when a chain is selected): focused nodes scale up, unfocused
//     compress to fill the remaining space, every focused node gets a min height
//     so a single line of label always fits
//
// All functions are pure — they take a snapshot of the d3-sankey output and
// return the baked positions. The component code then renders rects + links
// using the baked positions directly (no SVG parent transform), which means
// text glyphs stay at their natural font size and aren't stretched.
//
// Mirrors what the prototype HTML mockup proved out — verbatim ported here.

export interface BakedNode {
  id: string;
  stage: string;
  label: string;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export interface BakedLink {
  source: BakedNode;
  target: BakedNode;
  y0: number;
  y1: number;
  width: number;
  lbs: number;
  units: number;
  rows: number;
  index: number;
  linkKey: string;
}

export interface LayoutSnapshot {
  nodes: ReadonlyArray<{
    id: string;
    stage: string;
    label: string;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
    /** original height from d3-sankey output (pre-bake) */
    hOrig: number;
  }>;
  links: ReadonlyArray<{
    sourceId: string;
    targetId: string;
    value: number;
    lbs: number;
    units: number;
    rows: number;
  }>;
}

export interface BakeArgs {
  snapshot: LayoutSnapshot;
  stages: readonly string[];
  /** Vertical extent of the chart in CSS pixels. */
  height: number;
  top?: number;
  /** Zoom level — controls how much focused/unfocused expand or compress. */
  zoomK: number;
  /** Selected target — drives focus mode when present. */
  selected?: { kind: 'node'; nodeId: string } | { kind: 'link'; linkKey: string } | null;
  /**
   * Pre-computed set of focused node ids — wins over `selected` when provided.
   * Lets external chip/filter UIs trigger the same focus-mode reflow (grow
   * focused chain, squeeze unfocused, enforce min-height per focused node) as
   * a click-selection. When both are passed, the caller is expected to have
   * already composed the intended set (e.g. intersection of filter + click).
   */
  forcedHighlight?: ReadonlySet<string> | null;
  /** Minimum height per focused node in px (so the label always fits one line). */
  minFocusedNodeH?: number;
  /** Fraction of viewport the focused chain may claim at max zoom (default 0.92). */
  maxFocusedFraction?: number;
}

// Compute the set of node ids in the highlighted chain for the given selection.
// Traverses links both upstream and downstream from the selected node, or from
// both endpoints if a link was selected.
export function highlightedNodeIds(
  snapshot: LayoutSnapshot,
  selected: BakeArgs['selected'],
): Set<string> {
  const ids = new Set<string>();
  if (!selected) return ids;
  if (selected.kind === 'node') {
    ids.add(selected.nodeId);
    walkChain(snapshot, selected.nodeId, 'down', ids);
    walkChain(snapshot, selected.nodeId, 'up', ids);
  } else {
    const [src, tgt] = selected.linkKey.split('=>');
    if (src) ids.add(src);
    if (tgt) ids.add(tgt);
    if (src) walkChain(snapshot, src, 'up', ids);
    if (tgt) walkChain(snapshot, tgt, 'down', ids);
  }
  return ids;
}

function walkChain(
  snapshot: LayoutSnapshot,
  startId: string,
  dir: 'down' | 'up',
  ids: Set<string>,
): void {
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const l of snapshot.links) {
      if (dir === 'down' && l.sourceId === cur && !ids.has(l.targetId)) {
        ids.add(l.targetId);
        queue.push(l.targetId);
      } else if (dir === 'up' && l.targetId === cur && !ids.has(l.sourceId)) {
        ids.add(l.sourceId);
        queue.push(l.sourceId);
      }
    }
  }
}

// Bake positions for the current zoom + selection state.
export function bakeLayout(args: BakeArgs): { nodes: BakedNode[]; links: BakedLink[] } {
  const {
    snapshot,
    stages,
    height,
    top = 40,
    zoomK,
    selected,
    forcedHighlight,
    minFocusedNodeH = 14,
    maxFocusedFraction = 0.92,
  } = args;
  const bot = height - 40;
  const availH = Math.max(50, bot - top);
  // External `forcedHighlight` (e.g. from search-filter chips) takes priority —
  // it lets non-click sources trigger the same focus-mode reflow. When neither
  // a selection nor a forced set is supplied, focus mode stays off (uniform
  // column scaling).
  const hi: ReadonlySet<string> =
    forcedHighlight && forcedHighlight.size > 0
      ? forcedHighlight
      : selected
        ? highlightedNodeIds(snapshot, selected)
        : new Set<string>();
  const focusActive = hi.size > 0;

  // Build a Map of node id → baked output so we can populate y0/y1 then link
  // it into BakedLink afterwards.
  const baked = new Map<string, BakedNode>();

  // Per column reflow.
  for (const stage of stages) {
    const colNodes = snapshot.nodes
      .filter((n) => n.stage === stage)
      .slice()
      .sort((a, b) => a.y0 - b.y0);
    if (colNodes.length === 0) continue;
    const sumAll = colNodes.reduce((a, n) => a + n.hOrig, 0);
    if (sumAll === 0) continue;

    if (!focusActive) {
      // Uniform: every column fills availH × k.
      const scale = (availH * zoomK) / sumAll;
      let y = top;
      for (const n of colNodes) {
        const h = Math.max(0.5, n.hOrig * scale);
        baked.set(n.id, { id: n.id, stage: n.stage, label: n.label, x0: n.x0, x1: n.x1, y0: y, y1: y + h });
        y += h;
      }
      continue;
    }

    const focused = colNodes.filter((n) => hi.has(n.id));
    const unfocused = colNodes.filter((n) => !hi.has(n.id));
    const sumF = focused.reduce((a, n) => a + n.hOrig, 0);
    const sumU = unfocused.reduce((a, n) => a + n.hOrig, 0);
    const baseScale = availH / sumAll;

    let focusedScale: number;
    let unfocusedScale: number;
    if (focused.length === 0) {
      focusedScale = baseScale;
      unfocusedScale = baseScale;
    } else if (unfocused.length === 0) {
      focusedScale = Math.min(baseScale * zoomK, availH / sumF);
      unfocusedScale = 0;
    } else {
      // Lerp the focused chain's target height from natural (k=1) to
      // maxFocusedFraction × availH (k=10+). Floor at one line of text
      // per focused node so labels always fit.
      const naturalFocusedH = sumF * baseScale;
      const maxFocusedH = availH * maxFocusedFraction;
      const t = Math.max(0, Math.min(1, (zoomK - 1) / 9));
      let focusedTargetH = naturalFocusedH + (maxFocusedH - naturalFocusedH) * t;
      const minFocusedTotalH = focused.length * minFocusedNodeH;
      if (focusedTargetH < minFocusedTotalH) {
        focusedTargetH = Math.min(availH * 0.95, minFocusedTotalH);
      }
      const unfocusedTargetH = Math.max(8, availH - focusedTargetH);
      focusedScale = sumF > 0 ? focusedTargetH / sumF : 0;
      unfocusedScale = sumU > 0 ? unfocusedTargetH / sumU : 0;
    }

    // First pass: compute per-node heights honoring the focused min clamp.
    const heights = colNodes.map((n) => {
      const isF = hi.has(n.id);
      const rawH = n.hOrig * (isF ? focusedScale : unfocusedScale);
      return isF ? Math.max(minFocusedNodeH, rawH) : Math.max(0.5, rawH);
    });

    // When variance in hOrig pushes the per-focused-node clamp above the
    // intended focusedTargetH (or when there are simply too many focused
    // nodes to fit), the column total can exceed availH and the bottom
    // focused bars end up rendered below the chart's visible area. Squeeze
    // the unfocused band first so the focused chain keeps its min height;
    // only scale focused down when even crushing unfocused to zero isn't
    // enough.
    const focusedSum = heights.reduce(
      (a, h, i) => (hi.has(colNodes[i].id) ? a + h : a),
      0,
    );
    const unfocusedSum = heights.reduce(
      (a, h, i) => (hi.has(colNodes[i].id) ? a : a + h),
      0,
    );
    const totalH = focusedSum + unfocusedSum;
    if (totalH > availH) {
      if (focusedSum > availH) {
        // Pathological: chain itself exceeds availH at min height. Scale the
        // focused chain to fit; collapse unfocused to a hairline so they
        // remain clickable but invisible.
        const fK = availH / focusedSum;
        const dimMin = 0.25;
        for (let i = 0; i < heights.length; i++) {
          heights[i] = hi.has(colNodes[i].id)
            ? heights[i] * fK
            : dimMin;
        }
      } else {
        // Squeeze unfocused band to (availH - focusedSum) so focused keeps
        // its computed (clamped) height and the column never overflows.
        const unfocusedRoom = Math.max(0, availH - focusedSum);
        const uK = unfocusedSum > 0 ? unfocusedRoom / unfocusedSum : 0;
        for (let i = 0; i < heights.length; i++) {
          if (!hi.has(colNodes[i].id)) heights[i] *= uK;
        }
      }
    }

    let y = top;
    for (let i = 0; i < colNodes.length; i++) {
      const n = colNodes[i];
      const h = heights[i];
      baked.set(n.id, { id: n.id, stage: n.stage, label: n.label, x0: n.x0, x1: n.x1, y0: y, y1: y + h });
      y += h;
    }
  }

  // Re-route links: per source node, distribute outgoing links proportionally
  // along the source's right edge; same for incoming on the target side.
  // link width = min of source/target widths (visual consistency).
  type LinkRow = LayoutSnapshot['links'][number];
  const outgoingByNode = new Map<string, LinkRow[]>();
  const incomingByNode = new Map<string, LinkRow[]>();
  for (const l of snapshot.links) {
    if (!outgoingByNode.has(l.sourceId)) outgoingByNode.set(l.sourceId, []);
    if (!incomingByNode.has(l.targetId)) incomingByNode.set(l.targetId, []);
    outgoingByNode.get(l.sourceId)!.push(l);
    incomingByNode.get(l.targetId)!.push(l);
  }
  const srcWy = new Map<string, { y: number; w: number }>();
  const tgtWy = new Map<string, { y: number; w: number }>();
  for (const [nodeId, outs] of outgoingByNode) {
    const node = baked.get(nodeId);
    if (!node) continue;
    const sumOut = outs.reduce((a, l) => a + l.value, 0);
    const nodeH = node.y1 - node.y0;
    let yOut = node.y0;
    for (const l of outs) {
      const w = sumOut > 0 ? (l.value / sumOut) * nodeH : 0;
      srcWy.set(`${l.sourceId}=>${l.targetId}`, { y: yOut + w / 2, w });
      yOut += w;
    }
  }
  for (const [nodeId, ins] of incomingByNode) {
    const node = baked.get(nodeId);
    if (!node) continue;
    const sumIn = ins.reduce((a, l) => a + l.value, 0);
    const nodeH = node.y1 - node.y0;
    let yIn = node.y0;
    for (const l of ins) {
      const w = sumIn > 0 ? (l.value / sumIn) * nodeH : 0;
      tgtWy.set(`${l.sourceId}=>${l.targetId}`, { y: yIn + w / 2, w });
      yIn += w;
    }
  }

  const links: BakedLink[] = [];
  snapshot.links.forEach((l, idx) => {
    const src = baked.get(l.sourceId);
    const tgt = baked.get(l.targetId);
    if (!src || !tgt) return;
    const key = `${l.sourceId}=>${l.targetId}`;
    const sw = srcWy.get(key) ?? { y: (src.y0 + src.y1) / 2, w: 0 };
    const tw = tgtWy.get(key) ?? { y: (tgt.y0 + tgt.y1) / 2, w: 0 };
    links.push({
      source: src,
      target: tgt,
      y0: sw.y,
      y1: tw.y,
      width: Math.min(sw.w, tw.w),
      lbs: l.lbs,
      units: l.units,
      rows: l.rows,
      index: idx,
      linkKey: key,
    });
  });

  return { nodes: Array.from(baked.values()), links };
}

// Greedy word-wrap that splits on spaces. Used for in-bar multi-line labels.
export function wrapLabel(
  label: string,
  maxChars: number,
  maxLines: number,
): string[] {
  if (maxChars < 2 || maxLines === 0) return [];
  const words = String(label).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if (lines.length >= maxLines) break;
    if (w.length > maxChars) {
      if (line) {
        lines.push(line);
        line = '';
        if (lines.length >= maxLines) break;
      }
      lines.push(w.slice(0, maxChars - 1) + '…');
      continue;
    }
    const tryLine = line ? `${line} ${w}` : w;
    if (tryLine.length <= maxChars) {
      line = tryLine;
    } else {
      lines.push(line);
      line = w;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  // Truncation indicator when we ran out of room mid-label.
  if (lines.length === maxLines) {
    const shown = lines.join(' ').length;
    const wanted = words.join(' ').length;
    if (shown < wanted) {
      const last = lines[lines.length - 1];
      lines[lines.length - 1] = last.length > maxChars - 1
        ? last.slice(0, maxChars - 1) + '…'
        : last + '…';
    }
  }
  return lines;
}
